import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import mediaService from '../../services/mediaService';
import { useClient } from '../../contexts/ClientContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  Check, 
  AlertTriangle, 
  Loader2, 
  Image as ImageIcon, 
  Video, 
  FileText, 
  Music,
  Sparkles,
  FolderPlus,
  Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

interface ModernFileUploadProps {
  onClose: () => void;
  onUploadComplete: () => void;
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  preview?: string;
}

const ModernFileUpload: React.FC<ModernFileUploadProps> = ({ onClose, onUploadComplete }) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const { currentClient } = useClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate unique ID for files
  const generateId = () => Math.random().toString(36).substring(2, 11);

  // Create preview for image files
  const createPreview = (file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => resolve(undefined);
        reader.readAsDataURL(file);
      } else {
        resolve(undefined);
      }
    });
  };

  const uploadFilesAutomatically = useCallback(async (filesToUpload: UploadingFile[]) => {
    if (isUploading || filesToUpload.length === 0) return;

    setIsUploading(true);
    let successCount = 0;

    const uploadPromises = filesToUpload.map((uploadingFile) => {
      return (async () => {
        try {
          // Update status to uploading
          setUploadingFiles(prev =>
            prev.map((f) =>
              f.id === uploadingFile.id ? { ...f, status: 'uploading', progress: 0 } : f
            )
          );

          // Simulate realistic progress updates
          const progressInterval = setInterval(() => {
            setUploadingFiles(prev =>
              prev.map((f) =>
                f.id === uploadingFile.id && f.status === 'uploading'
                  ? { ...f, progress: Math.min(f.progress + Math.random() * 15 + 5, 90) }
                  : f
              )
            );
          }, 300);

          // Upload the file
          await mediaService.uploadFile(uploadingFile.file, currentClient?.id);

          // Clear progress interval and mark as completed
          clearInterval(progressInterval);
          setUploadingFiles(prev =>
            prev.map((f) =>
              f.id === uploadingFile.id ? { ...f, status: 'completed', progress: 100 } : f
            )
          );

          successCount++;
        } catch (error: any) {
          console.error('Upload error:', error);
          console.error('Error response data:', error.response?.data);
          console.error('Error status:', error.response?.status);

          let errorMessage = 'Upload failed';
          if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            console.log('Backend error detail:', detail);
            if (typeof detail === 'string') {
              errorMessage = detail;
            } else if (Array.isArray(detail)) {
              errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
            } else if (typeof detail === 'object') {
              errorMessage = detail.msg || detail.message || 'Validation error';
            }
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          setUploadingFiles(prev =>
            prev.map((f) =>
              f.id === uploadingFile.id
                ? { ...f, status: 'error', error: errorMessage }
                : f
            )
          );
        }
      })();
    });

    await Promise.all(uploadPromises);
    setIsUploading(false);

    // Show success toast
    if (successCount > 0) {
      toast({
        title: "Files Added!",
        description: `${successCount} file${successCount > 1 ? 's' : ''} added to your library.`
      });
    }

    // Auto-close modal if all files were uploaded successfully
    if (successCount > 0 && successCount === filesToUpload.length) {
      setTimeout(() => {
        onUploadComplete();
      }, 1500); // Give user time to see the completion
    }
  }, [currentClient, isUploading, onUploadComplete, toast]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newFiles = await Promise.all(
      acceptedFiles.map(async (file) => ({
        id: generateId(),
        file,
        progress: 0,
        status: 'pending' as const,
        preview: await createPreview(file),
      }))
    );
    
    setUploadingFiles(prev => [...prev, ...newFiles]);
    setIsDragOver(false);

    // Auto-upload files immediately after selection
    setTimeout(() => {
      uploadFilesAutomatically(newFiles);
    }, 100);
  }, [uploadFilesAutomatically]);

  // Custom file validation with conditional size limits
  const validateFile = (file: File) => {
    console.log('Validating file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeMB: (file.size / (1024 * 1024)).toFixed(2) + 'MB'
    });

    const fileSizeLimits = {
      image: 10 * 1024 * 1024,    // 10MB for images
      video: 100 * 1024 * 1024,   // 100MB for videos
      audio: 20 * 1024 * 1024,    // 20MB for audio
      model: 50 * 1024 * 1024,    // 50MB for 3D models
    };

    let fileType: keyof typeof fileSizeLimits = 'image';
    let maxSize = fileSizeLimits.image;

    if (file.type.startsWith('image/')) {
      fileType = 'image';
      maxSize = fileSizeLimits.image;
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
      maxSize = fileSizeLimits.video;
    } else if (file.type.startsWith('audio/')) {
      fileType = 'audio';
      maxSize = fileSizeLimits.audio;
    } else if (file.type === 'model/gltf-binary' || 
               file.type === 'model/gltf+json' || 
               file.name.toLowerCase().endsWith('.glb') || 
               file.name.toLowerCase().endsWith('.gltf') ||
               (file.type === 'application/octet-stream' && (file.name.toLowerCase().endsWith('.glb') || file.name.toLowerCase().endsWith('.gltf')))) {
      fileType = 'model';
      maxSize = fileSizeLimits.model;
    } else {
      console.log('File type not supported:', file.type, file.name);
      // Reject unsupported file types
      return {
        code: 'file-invalid-type',
        message: 'File type not supported. Please upload images, videos, audio, or 3D models only.'
      };
    }

    console.log('File classified as:', fileType, 'Max size:', (maxSize / (1024 * 1024)) + 'MB');

    if (file.size > maxSize) {
      console.log('File too large:', file.size, 'vs', maxSize);
      return {
        code: 'file-too-large',
        message: `File is too large. Maximum size for ${fileType} files is ${Math.round(maxSize / (1024 * 1024))}MB`
      };
    }

    console.log('File validation passed');
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'],
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.flac', '.aac'],
      'model/gltf-binary': ['.glb'],
      'model/gltf+json': ['.gltf'],
      'application/octet-stream': ['.glb'],
    },
    validator: validateFile,
    multiple: true,
    maxSize: 100 * 1024 * 1024, // 100MB max, let our validator handle specific limits
  });

  const removeFile = (id: string) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== id));
  };

  const retryUpload = (id: string) => {
    const fileToRetry = uploadingFiles.find(f => f.id === id);
    if (fileToRetry) {
      setUploadingFiles(prev =>
        prev.map(f => f.id === id ? { ...f, status: 'pending', error: undefined } : f)
      );
      uploadFilesAutomatically([{ ...fileToRetry, status: 'pending', error: undefined }]);
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onDrop(files);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-5 h-5" />;
    if (type.startsWith('video/')) return <Video className="w-5 h-5" />;
    if (type.startsWith('audio/')) return <Music className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-4 h-4 text-emerald-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'uploading':
        return <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />;
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed left-64 right-0 top-0 bottom-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 w-full max-w-6xl max-h-[85vh] overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
        >
          {/* Header */}
          <div className="relative p-4 pb-3 border-b border-slate-200/50 dark:border-slate-700/50">
            <div className="absolute -inset-2 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-xl blur-xl"></div>
            <div className="relative flex items-center justify-between p-4 bg-slate-100/30 dark:bg-slate-800/30 rounded-xl border border-slate-200/30 dark:border-slate-600/30">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                  <FolderPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-black bg-gradient-to-r from-slate-800 via-indigo-600 to-purple-600 dark:from-white dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent">
                    Add Media Files
                  </h2>
                  <p className="text-slate-600 dark:text-slate-300 text-sm">
                    Add files to your collection
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Drop Zone */}
          <div className="p-4">
            <motion.div
              {...getRootProps()}
              className={`relative overflow-hidden border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 ${
                isDragActive || isDragOver
                  ? 'border-indigo-500/50 bg-gradient-to-br from-indigo-50 via-purple-50/50 to-pink-50 dark:from-indigo-900/30 dark:via-purple-900/20 dark:to-pink-900/30 scale-[1.02] shadow-lg shadow-indigo-500/20'
                  : 'border-slate-300/50 dark:border-slate-600/50 hover:border-indigo-400/50 dark:hover:border-indigo-500/50 hover:bg-gradient-to-br hover:from-slate-50/50 hover:via-indigo-50/30 hover:to-purple-50/30 dark:hover:from-slate-800/30 dark:hover:via-indigo-900/20 dark:hover:to-purple-900/20'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <input {...getInputProps()} ref={fileInputRef} />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                multiple
                accept="image/*,video/*,audio/*,.glb,.gltf"
                className="hidden"
              />

              <div className="relative">
                <motion.div
                  className={`mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-3 transition-all duration-300 shadow-lg ${
                    isDragActive || isDragOver
                      ? 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-indigo-500/25'
                      : 'bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600'
                  }`}
                  animate={{
                    scale: isDragActive || isDragOver ? 1.1 : 1,
                    rotate: isDragActive || isDragOver ? [0, -5, 5, 0] : 0,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Upload className={`w-7 h-7 ${isDragActive || isDragOver ? 'text-white' : 'text-slate-500 dark:text-slate-400'}`} />
                </motion.div>

                <motion.h3 
                  className={`text-lg font-bold mb-2 transition-colors duration-300 ${
                    isDragActive || isDragOver
                      ? 'text-indigo-700 dark:text-indigo-300'
                      : 'text-slate-800 dark:text-slate-200'
                  }`}
                  animate={{
                    scale: isDragActive || isDragOver ? 1.05 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {isDragActive || isDragOver ? 'Drop files here!' : 'Add Your Media'}
                </motion.h3>

                <p className="text-slate-500 dark:text-slate-400 mb-4 text-sm">
                  {isDragActive || isDragOver
                    ? 'Release to add files automatically'
                    : 'Drag & drop files here or click to browse'}
                </p>

                <Button
                  type="button"
                  onClick={handleBrowseFiles}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-indigo-500/25 mb-4"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Browse Files
                </Button>

                <div className="flex flex-wrap justify-center gap-2 mb-3">
                  <div className="flex items-center px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs font-medium">
                    <ImageIcon className="w-3 h-3 mr-1" />
                    Images
                  </div>
                  <div className="flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-medium">
                    <Video className="w-3 h-3 mr-1" />
                    Videos
                  </div>
                  <div className="flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium">
                    <Music className="w-3 h-3 mr-1" />
                    Audio
                  </div>
                  <div className="flex items-center px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-medium">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
                    </svg>
                    3D Models
                  </div>
                </div>
                
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  Images: 10MB • Videos: 100MB • Audio: 20MB • 3D Models: 50MB • Multiple files supported
                </div>
              </div>
            </motion.div>
          </div>

          {/* File List */}
          <AnimatePresence>
            {uploadingFiles.length > 0 && (
              <motion.div
                className="px-4 pb-4"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="border-t border-slate-200/50 dark:border-slate-700/50 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-indigo-500" />
                      Processing Files ({uploadingFiles.length})
                    </h3>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {uploadingFiles.filter(f => f.status === 'completed').length} of {uploadingFiles.length} completed
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-80 overflow-y-auto">
                    {uploadingFiles.map((uploadingFile) => (
                      <motion.div
                        key={uploadingFile.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="relative overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-4 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        {/* Progress background */}
                        {uploadingFile.status === 'uploading' && (
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadingFile.progress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        )}

                        <div className="relative flex items-center space-x-4">
                          {/* File preview or icon */}
                          <div className="flex-shrink-0">
                            {uploadingFile.preview ? (
                              <img
                                src={uploadingFile.preview}
                                alt={uploadingFile.file.name}
                                className="w-16 h-16 object-cover rounded-xl shadow-sm"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 rounded-xl flex items-center justify-center shadow-sm">
                                {getFileIcon(uploadingFile.file.type)}
                              </div>
                            )}
                          </div>

                          {/* File info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate mb-1">
                              {uploadingFile.file.name}
                            </p>
                            <div className="flex items-center space-x-3 mb-2">
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {mediaService.formatFileSize(uploadingFile.file.size)}
                              </p>
                              {uploadingFile.status === 'uploading' && (
                                <span className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">
                                  {Math.round(uploadingFile.progress)}%
                                </span>
                              )}
                            </div>
                            
                            {/* Progress bar */}
                            {uploadingFile.status === 'uploading' && (
                              <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2 mb-2">
                                <motion.div
                                  className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-2 rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${uploadingFile.progress}%` }}
                                  transition={{ duration: 0.3 }}
                                />
                              </div>
                            )}
                            
                            {uploadingFile.error && (
                              <p className="text-xs text-red-500 dark:text-red-400 truncate">
                                {uploadingFile.error}
                              </p>
                            )}
                          </div>

                          {/* Status and actions */}
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(uploadingFile.status)}
                            
                            {/* Action buttons */}
                            {uploadingFile.status === 'error' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => retryUpload(uploadingFile.id)}
                                className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                              >
                                Retry
                              </Button>
                            )}
                            {(uploadingFile.status === 'pending' || uploadingFile.status === 'error') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFile(uploadingFile.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <motion.div 
            className="flex justify-between items-center p-6 border-t border-slate-200/50 dark:border-slate-700/50 bg-gradient-to-r from-slate-50/50 via-indigo-50/30 to-purple-50/30 dark:from-slate-800/30 dark:via-indigo-900/20 dark:to-purple-900/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-center space-x-4">
              {uploadingFiles.length > 0 && (
                <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {uploadingFiles.filter(f => f.status === 'completed').length} of {uploadingFiles.length} completed
                </div>
              )}
              {isUploading && (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                  <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">Processing...</span>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="ghost" 
                onClick={onClose}
                className="text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 px-6 py-2 rounded-xl"
              >
                {isUploading ? 'Close When Done' : 'Close'}
              </Button>
              {uploadingFiles.length > 0 && !isUploading && (
                <Button 
                  onClick={onUploadComplete}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/25"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Done
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ModernFileUpload;